import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';


import { cleanEntity } from '../shared/util/entity-utils/entity-utils';
import { createEntitySlice, serializeAxiosError } from '../shared/reducers/reducer.utils';


const initialState = {
  loading: false,
  errorMessage: null,
  entities: [],
  proveedores: [],
  servicios: [],
  entity: {},
  updating: false,
  totalItems: 0,
  updateSuccess: false,
} as any;

const apiUrl = 'api/empresas';
/** Listado agrupado / vista empresas en /empresas (índice y /grupo/new). */
const apiUrlEmpresasGrupos = 'api/empresas/grupos';

// Actions

export const getEntities = createAsyncThunk(
  'empresa/fetch_entity_list',
  async ({ page, size, sort, filters, listPath }: any) => {
    const baseUrl = listPath === 'grupos' ? apiUrlEmpresasGrupos : apiUrl;

    const params = new URLSearchParams();

    filters?.forEach((e: any) => {
      params.append(e.filterName, String(e.filterValue));
    });

    // agregar paginación y sort
    params.append("page", String(page));
    params.append("size", String(size));
    params.append("sort", sort);

    const filtersObject = Object.fromEntries(
      (filters ?? []).map((e: any) => [e.filterName, e.filterValue])
    );



    console.log(`query ${params}`);

    const requestUrl = `${baseUrl}?${params}&cacheBuster=${new Date().getTime()}`;
    return axios.get<any[]>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'empresa/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<any>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getProveedores = createAsyncThunk(
  'empresa/fetch_proveedores',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}/proveedor`;
    return axios.get<any>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createProveedorEmpresa = createAsyncThunk(
  'empresa/create_proveedor_empresa',
  async (
    {
      empresaId,
      proveedorId,
      alta,
      alias,
      contadores,
      contactos,
    }: {
      empresaId: number;
      proveedorId: number;
      alta?: string;
      alias?: string;
      contadores?: { usuario: Record<string, unknown> }[];
      contactos?: {
        contacto: Record<string, unknown>;
        correo: string;
        repDocs: boolean;
        recordatorio: boolean;
      }[];
    },
    thunkAPI,
  ) => {
    const payload: Record<string, unknown> = {
      empresa: { id: empresaId },
      proveedor: { id: proveedorId },
    };
    if (alta != null && alta.trim() !== '') {
      payload.alta = alta.trim();
    }
    if (alias != null && alias.trim() !== '') {
      payload.alias = alias.trim();
    }
    if (contadores != null && contadores.length > 0) {
      payload.contadores = contadores;
    }
    if (contactos != null && contactos.length > 0) {
      payload.contactos = contactos;
    }
    const result = await axios.post<any>('api/proveedor-empresas', cleanEntity(payload));
    thunkAPI.dispatch(getProveedores(empresaId));
    return result;
  },
  { serializeError: serializeAxiosError },
);

function serviciosPreciosFromApiPayload(data: unknown): any[] {
  if (Array.isArray(data)) return data;
  if (data != null && typeof data === 'object') {
    const body = data as { content?: unknown[]; data?: unknown[] };
    if (Array.isArray(body.content)) return body.content;
    if (Array.isArray(body.data)) return body.data;
  }
  return [];
}

export const getServicios = createAsyncThunk(
  'empresa/fetch_servicios',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}/servicios-precios`;
    return axios.get<any>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'empresa/create_entity',
  async (entity: any, thunkAPI) => {
    const result = await axios.post<any>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'empresa/update_entity',
  async (entity: any, thunkAPI) => {
    const result = await axios.put<any>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'empresa/partial_update_entity',
  async (entity: any, thunkAPI) => {
    const result = await axios.patch<any>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'empresa/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<any>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const EmpresaSlice = createEntitySlice({
  name: 'empresa',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getProveedores), (state, action) => {
        const { data, headers } = action.payload;
        return {
          ...state,
          loading: false,
          proveedores: data
        };
      })
      .addMatcher(isFulfilled(getServicios), (state, action) => {
        const { data } = action.payload;
        return {
          ...state,
          loading: false,
          servicios: serviciosPreciosFromApiPayload(data),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(createProveedorEmpresa), state => {
        state.updating = false;
        state.updateSuccess = true;
      })
      .addMatcher(isPending(getEntities, getEntity, getProveedores, getServicios), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })

      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity, createProveedorEmpresa), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = EmpresaSlice.actions;

// Reducer
export default EmpresaSlice.reducer;
